import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Heart,
  ShoppingBag,
  Trash2,
  ArrowRight,
  Sparkles,
  Check,
} from "lucide-react";

import serumImg from "../assests/pro4.webp"; // Apna asset path check kar lein

const Wishlist = () => {
  // Sample Initial Wishlist Products State
  const [wishlistItems, setWishlistItems] = useState([
    {
      id: "glow-serum",
      name: "Glow Niacinamide Serum",
      category: "Serums & Oils",
      price: 48.0,
      image: serumImg,
      inStock: true,
      tag: "Bestseller",
    },
    {
      id: "barrier-cream",
      name: "Ceramide Hydrating Barrier Cream",
      category: "Moisturizers",
      price: 52.0,
      image: serumImg,
      inStock: true,
      tag: "New",
    },
    {
      id: "rice-toner",
      name: "Radiant Rice Essence Toner",
      category: "Cleansers & Toners",
      price: 36.0,
      image: serumImg,
      inStock: false,
      tag: "Trending",
    },
  ]);

  const [addedToCartId, setAddedToCartId] = useState(null);

  // Remove Item Handler
  const handleRemove = (id) => {
    setWishlistItems(wishlistItems.filter((item) => item.id !== id));
  };

  // Add to Cart Simulation
  const handleAddToCart = (id) => {
    setAddedToCartId(id);
    setTimeout(() => setAddedToCartId(null), 2000);
  };

  return (
    <div className="min-h-[calc(100vh-80px)] bg-[#FAF8F5] py-12 px-4 sm:px-6 lg:px-8 flex justify-center">
      <div className="w-full max-w-7xl space-y-8">
        
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-[#E8E2D9] pb-6">
          <div>
            <div className="flex items-center gap-2 text-[#9D7D4F] text-xs font-semibold uppercase tracking-[2px] mb-1">
              <Sparkles size={13} /> Saved Formulations
            </div>
            <h1 className="font-serif text-3xl font-semibold text-[#1F1B18]">
              My Wishlist
            </h1>
          </div>
          <p className="text-xs text-gray-500 font-medium">
            {wishlistItems.length} {wishlistItems.length === 1 ? "Item" : "Items"} Saved
          </p>
        </div>

        {/* Empty Wishlist View */}
        {wishlistItems.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 text-center border border-[#E8E2D9] shadow-sm flex flex-col items-center justify-center space-y-4 my-8">
            <div className="w-16 h-16 rounded-full bg-[#F7F4EF] border border-[#E8E2D9] flex items-center justify-center text-[#9D7D4F]">
              <Heart size={28} />
            </div>
            <h3 className="font-serif text-xl font-semibold text-[#1F1B18]">
              Your Wishlist is Empty
            </h3>
            <p className="text-xs text-gray-500 max-w-sm leading-relaxed">
              Explore our luxury Seoul-inspired skincare rituals and save your favorite formulations for later.
            </p>
            <Link
              to="/shop"
              className="mt-2 inline-flex items-center gap-2 bg-[#1F1B18] hover:bg-[#9D7D4F] text-white px-6 py-3 rounded-xl text-xs uppercase tracking-widest font-semibold transition-colors duration-300 shadow-md"
            >
              <span>Explore Products</span>
              <ArrowRight size={14} />
            </Link>
          </div>
        ) : (
          /* Wishlist Items Grid */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {wishlistItems.map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-2xl border border-[#E8E2D9] overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between group"
              >
                {/* Image Container */}
                <div className="relative aspect-square bg-[#F7F4EF] overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                  />

                  {/* Badge */}
                  {item.tag && (
                    <span className="absolute top-3 left-3 bg-white/90 backdrop-blur-md text-[#1F1B18] text-[9px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full border border-[#E8E2D9]">
                      {item.tag}
                    </span>
                  )}

                  {/* Remove Button */}
                  <button
                    onClick={() => handleRemove(item.id)}
                    className="absolute top-3 right-3 p-2 bg-white/90 backdrop-blur-md rounded-full text-gray-400 hover:text-red-600 transition duration-200 shadow-sm"
                    title="Remove from Wishlist"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>

                {/* Content Area */}
                <div className="p-5 flex-1 flex flex-col justify-between space-y-4">
                  <div>
                    <span className="text-[10px] uppercase tracking-widest text-[#9D7D4F] font-semibold block mb-1">
                      {item.category}
                    </span>
                    <Link
                      to={`/product/${item.id}`}
                      className="font-serif font-medium text-base text-[#1F1B18] hover:text-[#9D7D4F] transition line-clamp-1"
                    >
                      {item.name}
                    </Link>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-sm font-semibold text-[#1F1B18]">
                        ${item.price.toFixed(2)}
                      </p>
                      <span
                        className={`text-[10px] uppercase tracking-wider font-semibold ${
                          item.inStock ? "text-emerald-600" : "text-amber-600"
                        }`}
                      >
                        {item.inStock ? "In Stock" : "Out of Stock"}
                      </span>
                    </div>
                  </div>

                  {/* Add to Cart Button */}
                  <button
                    onClick={() => handleAddToCart(item.id)}
                    disabled={!item.inStock}
                    className={`w-full py-2.5 rounded-xl text-xs uppercase tracking-widest font-semibold flex items-center justify-center gap-2 transition duration-300 ${
                      !item.inStock
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200"
                        : addedToCartId === item.id
                        ? "bg-emerald-600 text-white"
                        : "bg-[#1F1B18] hover:bg-[#9D7D4F] text-white shadow-sm"
                    }`}
                  >
                    {addedToCartId === item.id ? (
                      <>
                        <Check size={14} />
                        <span>Added to Cart</span>
                      </>
                    ) : (
                      <>
                        <ShoppingBag size={14} />
                        <span>{item.inStock ? "Add to Cart" : "Sold Out"}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

export default Wishlist;